import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LoadingSpinner from './LoadingSpinner';

import {
  setIsActiveChat,
  setSelectedTileId,
} from '../reducers/uiReducer';
import { setUserDatasets } from '../reducers/datasetsReducer';
import { addMessage, selectChatContext } from '../reducers/messagesReducer';
import { sendMessage, fetchDatasets } from '../services/apiService';
import { setLoading } from '../reducers/loadingReducer';
import { showToast } from '../reducers/uiReducer';

const DataTable = ({
  userDatasets,
  handleSelectedRow,
  handleShowAddDataset,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const chatContext = useSelector(selectChatContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDatasets, setFilteredDatasets] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);

  const fetchUserDatasets = async () => {
    setIsLoading(true);
    try {
      const response = await fetchDatasets();
      if (response && response.datasets) {
        dispatch(setUserDatasets(response.datasets));
        setFilteredDatasets(response.datasets);
      } else {
        setFilteredDatasets([]);
      }
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
      setFilteredDatasets([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDatasets();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (Array.isArray(filteredDatasets)) {
        const filtered = userDatasets.filter(
          (prompt) =>
            prompt.dataSetName &&
            prompt.dataSetName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredDatasets(filtered);
      }
    }, 200);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, userDatasets]);

  const handleRunPrompt = async (prompt) => {
    dispatch(setSelectedTileId(null));
    dispatch(setIsActiveChat(true));
    dispatch(setLoading(true));

    const newUserMessage = {
      role: 'user',
      componentType: 'text',
      content: [{ type: 'text', text: prompt.promptText }],
    };

    const updatedChatContext = [...chatContext, newUserMessage];

    dispatch(addMessage(newUserMessage));

    try {
      const response = await sendMessage(updatedChatContext);
      if (response && response.choices?.[0]?.message) {
        const assistantMessage = {
          role: 'assistant',
          content: [
            { type: 'text', text: response.choices[0].message.content },
          ],
        };
        dispatch(addMessage(assistantMessage));
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        role: 'error',
        content: [{ type: 'text', text: 'Service Unavailable.' }],
      };
      dispatch(addMessage(errorMessage));
      dispatch(showToast({ message: 'Failed to send message', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRowClick = (row) => {
    if (selectedDataset === row) {
      setSelectedDataset(null);
      handleSelectedRow(null);
    } else {
      setSelectedDataset(row);
      handleSelectedRow(row);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading}
        />
        <Tooltip title="Upload new document">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleShowAddDataset}
            disabled={true}
          >
            Dataset
          </Button>
        </Tooltip>
      </Box>

      <TableContainer
        sx={{
          maxHeight: 400,
          overflowY: 'auto',
          minHeight: 300,
          border: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 10, height: '100%' }}
          >
            <LoadingSpinner size={30} />
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Dataset</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Data Source</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: 16 }}>Record Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(filteredDatasets) &&
                filteredDatasets.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        selectedDataset === row
                          ? theme.palette.action.selected
                          : index % 2 === 0
                            ? theme.palette.action.hover
                            : 'inherit',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>{row.dataSetName}</TableCell>
                    <TableCell>{row.dataSource}</TableCell>
                    <TableCell>{row.dataSourceConfig.sourceRecordSize}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default DataTable;
